import React from 'react';
import { Download, Upload, Heart, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DuplicateConfirmationModal from '@/components/common/DuplicateConfirmationModal';
import BulkUploadPreview from '@/components/common/BulkUploadPreview';
import useBulkUpload from '@/hooks/useBulkUpload';

/**
 * Activities Bulk Upload Component
 * Refactored to use the reusable useBulkUpload hook
 * Now includes Duplicate Confirmation Modal (Task 1)
 */
const ActivitiesBulkUpload = () => {
  // Initialize the hook with activities-specific configuration
  const {
    // State
    filters,
    classes,
    wardens,
    downloading,
    uploading,
    previewData,
    showDuplicateModal,

    // Actions
    updateFilter,
    handleDownloadTemplate,
    handleFileUpload,
    confirmUpload,
    closeDuplicateModal,

    // Utilities
    getTodayDate,

    // Config
    useEnhancedPreview
  } = useBulkUpload({
    templateEndpoint: '/v1/admin/bulk-upload/templates/activities-weekly',
    previewEndpoint: '/v1/admin/bulk-upload/activities/preview',
    confirmEndpoint: '/v1/admin/bulk-upload/activities/confirm',
    successMessage: 'activity records',
    useWeekFilter: true,
    downloadFileNamePattern: (filters) => 
      `activities_week_${filters.weekStart}_to_${filters.weekEnd}.xlsx`,
    useEnhancedPreview: true  // Enable enhanced preview
  });

  // Handle date change with future date restriction
  const handleDateChange = (field, value) => {
    const today = getTodayDate();
    if (value > today) {
      return; // Silently ignore future dates
    }
    updateFilter(field, value);
  };

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-6">
          <Heart className="w-6 h-6" style={{ color: '#2C5F7F' }} />
          <h2 className="text-2xl font-bold" style={{ color: '#2C5F7F' }}>
            Weekly Activities Upload
          </h2>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium mb-2">Week Start Date</label>
            <input
              type="date"
              value={filters.weekStart}
              max={getTodayDate()}
              onChange={(e) => handleDateChange('weekStart', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Week End Date</label>
            <input
              type="date"
              value={filters.weekEnd}
              max={getTodayDate()}
              onChange={(e) => handleDateChange('weekEnd', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium mb-2">Filter by Class (Optional)</label>
            <select
              value={filters.classFilter}
              onChange={(e) => updateFilter('classFilter', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Classes</option>
              {classes.map((cls) => (
                <option key={cls.id} value={cls.name}>
                  {cls.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Filter by Warden (Optional)</label>
            <select
              value={filters.wardenFilter}
              onChange={(e) => updateFilter('wardenFilter', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Wardens</option>
              {wardens.map((warden) => (
                <option key={warden.id} value={warden.id}>
                  {warden.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-4">
          <Button
            onClick={handleDownloadTemplate}
            disabled={downloading}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Download className="w-4 h-4" />
            {downloading ? 'Downloading...' : 'Download Template'}
          </Button>

          <label className="cursor-pointer">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              uploading 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}>
              <Upload className="w-4 h-4" />
              {uploading ? 'Uploading...' : 'Upload File'}
            </div>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileUpload}
              className="hidden"
              disabled={uploading}
            />
          </label>
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-purple-800">
              <p className="font-semibold mb-2">Instructions:</p>
              <ol className="list-decimal list-inside space-y-1 ml-4">
                <li>Select the week range for activity records</li>
                <li>Optionally filter by class or warden to get a filtered template</li>
                <li>Download the template Excel file</li>
                <li>Fill in activity details for each student and date</li>
                <li>Activity types: Sports, Arts, Academic, Cultural, Other</li>
                <li>Upload the completed file</li>
                <li>Choose to skip or overwrite duplicate activities</li>
              </ol>
              <p className="mt-2 text-xs text-purple-600">
                💡 Tip: Duplicate detection helps avoid recording the same activity twice.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Modal - Enhanced or Simple */}
      {showDuplicateModal && previewData && (
        useEnhancedPreview ? (
          <BulkUploadPreview
            isOpen={showDuplicateModal}
            onClose={closeDuplicateModal}
            previewData={previewData}
            onConfirm={confirmUpload}
            moduleName="Activities"
          />
        ) : (
          <DuplicateConfirmationModal
            isOpen={showDuplicateModal}
            onClose={closeDuplicateModal}
            duplicateCount={previewData.duplicate_count}
            newRecordsCount={previewData.new_records?.length || 0}
            onConfirm={confirmUpload}
            moduleName="Activities"
          />
        )
      )}
    </div>
  );
};

export default ActivitiesBulkUpload;
